import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Trash2,
  RefreshCw,
  Smartphone,
  Laptop,
  Monitor,
  Activity,
  AlertCircle,
  Download,
  Upload,
  Edit,
  Ban,
  ShieldCheck,
} from 'lucide-react';
import StatusBadge from '../components/common/StatusBadge';
import ConfirmDialog from '../components/common/ConfirmDialog';
import Modal from '../components/common/Modal';
import { appDeviceService, routingPolicyService } from '../services';
import type { AppDeviceDetail, RoutingPolicy } from '../services';
import type { DeviceGroup } from '../services/appDevice.service';
import { Route } from 'lucide-react';
import { useToast } from '../components/common/ToastContainer';
import { formatDate } from '../utils/formatDate';
import { appDeviceProtocolLabel } from '../utils/appDeviceProtocol';
import { useSettingsStore } from '../stores/settingsStore';
import { usePermissions } from '../hooks/usePermissions';

const AppDeviceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { getDateFormatSettings } = useSettingsStore();
  const { canUpdate, canDelete } = usePermissions();
  const [deviceDetail, setDeviceDetail] = useState<AppDeviceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [showReenableConfirm, setShowReenableConfirm] = useState(false);
  const [reenabling, setReenabling] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [availableGroups, setAvailableGroups] = useState<DeviceGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [assigningGroup, setAssigningGroup] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editName, setEditName] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [routingPolicies, setRoutingPolicies] = useState<RoutingPolicy[]>([]);
  const [loadingPolicies, setLoadingPolicies] = useState(false);
  const [groupNameMap, setGroupNameMap] = useState<Map<string, DeviceGroup>>(new Map());

  useEffect(() => {
    if (!id) return;
    fetchDeviceDetail();
    // Fetch groups for name resolution
    appDeviceService.listDeviceGroups({ page: 1, limit: 200 })
      .then((res) => {
        const map = new Map<string, DeviceGroup>();
        for (const g of res.items) map.set(g.id, g);
        setGroupNameMap(map);
      })
      .catch(() => {});
  }, [id]);

  // Fetch routing policies linked to device's group
  useEffect(() => {
    const fetchRoutingPolicies = async () => {
      if (!deviceDetail?.device.deviceGroupId) {
        setRoutingPolicies([]);
        return;
      }

      try {
        setLoadingPolicies(true);
        // Fetch DG-scoped policies for this tenant
        const allPolicies = await routingPolicyService.list({
          pageSize: 100,
          scope: 'device_group',
        });
        // Filter to policies linked to this device's group via routing_policy_ids
        const group = groupNameMap.get(deviceDetail.device.deviceGroupId);
        const groupPolicyIds = group?.routing_policy_ids || [];
        const applicable = allPolicies.items.filter(p =>
          groupPolicyIds.includes(p.id)
        );
        setRoutingPolicies(applicable);
      } catch (err) {
        console.error('Failed to fetch routing policies:', err);
      } finally {
        setLoadingPolicies(false);
      }
    };

    fetchRoutingPolicies();
  }, [deviceDetail?.device.deviceGroupId, groupNameMap]);

  const fetchDeviceDetail = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await appDeviceService.getDeviceById(id);
      setDeviceDetail(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load device details');
      toast.error('Error', err.message || 'Failed to load device details');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      setDeleting(true);
      await appDeviceService.deleteDevice(id);
      toast.success('Device Deleted', 'Device and all its peers have been removed');
      navigate('/app-devices');
    } catch (err: any) {
      toast.error('Delete Failed', err.message || 'Failed to delete device');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleRevoke = async () => {
    if (!id) return;
    try {
      setRevoking(true);
      await appDeviceService.revokeDevice(id);
      toast.success('Device Revoked', 'VPN access has been revoked for this device');
      await fetchDeviceDetail();
    } catch (err: any) {
      toast.error('Revoke Failed', err.response?.data?.message || err.message || 'Failed to revoke device');
    } finally {
      setRevoking(false);
      setShowRevokeConfirm(false);
    }
  };

  const handleReenable = async () => {
    if (!id) return;
    try {
      setReenabling(true);
      await appDeviceService.reenableDevice(id);
      toast.success('Device Re-enabled', 'Access block lifted. The user must re-register from the app to reconnect.');
      await fetchDeviceDetail();
    } catch (err: any) {
      toast.error('Re-enable Failed', err.response?.data?.message || err.message || 'Failed to re-enable device');
    } finally {
      setReenabling(false);
      setShowReenableConfirm(false);
    }
  };

  const handleOpenGroupModal = async () => {
    try {
      const response = await appDeviceService.listDeviceGroups({ page: 1, limit: 100 });
      setAvailableGroups(response.items);
      setSelectedGroupId(deviceDetail?.device.deviceGroupId || '');
      setShowGroupModal(true);
    } catch (err: any) {
      toast.error('Error', 'Failed to load device groups');
    }
  };

  const handleAssignGroup = async () => {
    if (!id) return;

    try {
      setAssigningGroup(true);
      if (selectedGroupId) {
        await appDeviceService.assignDeviceToGroup(id, selectedGroupId);
        toast.success('Group Assigned', 'Device assigned to group successfully');
      } else {
        await appDeviceService.removeDeviceFromGroup(id);
        toast.success('Group Removed', 'Device removed from group');
      }
      await fetchDeviceDetail();
      setShowGroupModal(false);
    } catch (err: any) {
      toast.error('Error', err.message || 'Failed to update group assignment');
    } finally {
      setAssigningGroup(false);
    }
  };

  const handleOpenEditModal = () => {
    if (deviceDetail) {
      setEditName(deviceDetail.device.name);
      setShowEditModal(true);
    }
  };

  const handleSaveEdit = async () => {
    if (!id || !editName.trim()) {
      toast.error('Validation Error', 'Device name is required');
      return;
    }

    try {
      setSavingEdit(true);
      await appDeviceService.updateDevice(id, { deviceName: editName.trim() });
      toast.success('Device Updated', 'Device name updated successfully');
      await fetchDeviceDetail();
      setShowEditModal(false);
    } catch (err: any) {
      toast.error('Update Failed', err.message || 'Failed to update device');
    } finally {
      setSavingEdit(false);
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'ios':
        return <Smartphone className="w-6 h-6 text-gray-600" />;
      case 'android':
        return <Smartphone className="w-6 h-6 text-green-600" />;
      case 'windows':
        return <Monitor className="w-6 h-6 text-blue-600" />;
      case 'macos':
        return <Laptop className="w-6 h-6 text-gray-700" />;
      case 'linux':
        return <Monitor className="w-6 h-6 text-orange-600" />;
      default:
        return <Monitor className="w-6 h-6 text-gray-600" />;
    }
  };

  const formatBytes = (bytes?: number): string => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !deviceDetail) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Device</h2>
          <p className="text-gray-600 mb-4">{error || 'Device not found'}</p>
          <button onClick={() => navigate('/app-devices')} className="btn-primary">
            Back to Devices
          </button>
        </div>
      </div>
    );
  }

  const { device, peers } = deviceDetail;
  const activePeer = peers.find((p) => p.enabled && p.status === 'active');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/app-devices')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center space-x-3">
            {getPlatformIcon(device.platform)}
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{device.name}</h1>
              <p className="text-sm text-gray-500">{device.fingerprint}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button onClick={fetchDeviceDetail} className="btn-secondary inline-flex items-center space-x-2">
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
          {canUpdate && (
            <button onClick={handleOpenEditModal} className="btn-secondary inline-flex items-center space-x-2">
              <Edit className="w-4 h-4" />
              <span>Edit</span>
            </button>
          )}
          {canUpdate && (device.status === 'revoked' ? (
            <button
              onClick={() => setShowReenableConfirm(true)}
              className="btn-secondary inline-flex items-center space-x-2 text-blue-700 border-blue-200 hover:bg-blue-50"
            >
              <ShieldCheck className="w-4 h-4" />
              <span>Re-enable</span>
            </button>
          ) : (
            <button
              onClick={() => setShowRevokeConfirm(true)}
              className="btn-secondary inline-flex items-center space-x-2 text-red-700 border-red-200 hover:bg-red-50"
            >
              <Ban className="w-4 h-4" />
              <span>Revoke</span>
            </button>
          ))}
          {canDelete && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="btn-danger inline-flex items-center space-x-2"
            >
              <Trash2 className="w-4 h-4" />
              <span>Delete Device</span>
            </button>
          )}
        </div>
      </div>

      {/* Status Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div>
            <p className="text-sm text-gray-600 mb-2">Status</p>
            <StatusBadge
              status={
                device.status === 'active' ? 'Online'
                  : device.status === 'revoked' ? 'Revoked'
                  : device.status === 'suspended' ? 'Disconnect'
                  : 'Offline'
              }
            />
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-2">Platform</p>
            <p className="text-lg font-medium text-gray-900">{device.platform}</p>
            {device.osVersion && <p className="text-sm text-gray-500">{device.osVersion}</p>}
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-2">App Version</p>
            <p className="text-lg font-medium text-gray-900">{device.appVersion || 'Unknown'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-2">Last Seen</p>
            <p className="text-lg font-medium text-gray-900">
              {device.lastSeenAt ? formatDate(device.lastSeenAt, getDateFormatSettings()) : 'Never'}
            </p>
          </div>
        </div>
      </div>

      {/* Device Information */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Device Information</h2>
        </div>
        <div className="p-6">
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <dt className="text-sm text-gray-600 mb-1">Device ID</dt>
              <dd className="text-sm font-mono text-gray-900">{device.id}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-600 mb-1">Owner</dt>
              <dd className="text-sm text-gray-900">
                {device.ownerName || device.ownerEmail || device.ownerId}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-gray-600 mb-1">Fingerprint</dt>
              <dd className="text-sm font-mono text-gray-900">{device.fingerprint}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-600 mb-1">Device Group</dt>
              <dd className="flex items-center space-x-2">
                <span className="text-sm text-gray-900">
                  {device.deviceGroupId
                    ? groupNameMap.get(device.deviceGroupId)?.name || 'Unknown Group'
                    : 'Unassigned'}
                </span>
                {device.deviceGroupId && groupNameMap.get(device.deviceGroupId)?.edge_type === 'access' ? (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700">
                    ZTNA
                  </span>
                ) : device.deviceGroupId && groupNameMap.get(device.deviceGroupId)?.edge_id && (
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                    groupNameMap.get(device.deviceGroupId)?.edge_type === 'mtge'
                      ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {groupNameMap.get(device.deviceGroupId)?.edge_type === 'mtge' ? 'MTGE' : 'Edge'}
                  </span>
                )}
                <button
                  onClick={handleOpenGroupModal}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                >
                  Change
                </button>
              </dd>
            </div>
            <div>
              <dt className="text-sm text-gray-600 mb-1">Created At</dt>
              <dd className="text-sm text-gray-900">{formatDate(device.createdAt, getDateFormatSettings())}</dd>
            </div>
            {device.updatedAt && (
              <div>
                <dt className="text-sm text-gray-600 mb-1">Updated At</dt>
                <dd className="text-sm text-gray-900">{formatDate(device.updatedAt, getDateFormatSettings())}</dd>
              </div>
            )}
            <div>
              <dt className="text-sm text-gray-600 mb-1">Tunnel Protocol</dt>
              <dd className="text-sm font-mono text-gray-900 bg-gray-100 inline-block px-2 py-0.5 rounded">
                {appDeviceProtocolLabel(device, 'long')}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Routing Policies */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
            <Route className="w-5 h-5 text-primary-600" />
            <span>Routing Policies</span>
          </h2>
        </div>
        <div className="p-6">
          {loadingPolicies ? (
            <div className="text-center text-gray-500 py-4">Loading policies...</div>
          ) : routingPolicies.length === 0 ? (
            <div className="text-center text-gray-500 py-4">
              <Route className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No routing policies apply to this device</p>
              <p className="text-xs text-gray-400 mt-1">
                {device.deviceGroupId
                  ? 'Add policies to this device\'s group in the Device Groups page'
                  : 'Assign this device to a group to apply routing policies'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {routingPolicies.map((policy) => (
                <div
                  key={policy.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-2 h-2 rounded-full ${policy.enabled === 1 ? 'bg-green-500' : 'bg-gray-400'}`} />
                    <div>
                      <div className="font-medium text-gray-900">{policy.name}</div>
                      {policy.description && (
                        <div className="text-xs text-gray-500">{policy.description}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-xs text-gray-500">
                      Priority: {policy.priority} | {policy.rules?.length || 0} rules
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${policy.enabled === 1 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                      {policy.enabled === 1 ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Secure Access connection (VSNP-1626 W.6): an Access-plane device has
          no v2 peer; the API reads its live state from Secure Access. */}
      {device.access && (
        <div className="bg-white rounded-lg shadow" data-testid="app-device-access-connection">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
              <ShieldCheck className={`w-5 h-5 ${device.access.connected ? 'text-green-500' : 'text-gray-400'}`} />
              <span>Secure Access Connection</span>
            </h2>
          </div>
          <div className="p-6">
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <dt className="text-sm text-gray-600 mb-1">Status</dt>
                <dd><StatusBadge status={device.access.connected ? 'Online' : 'Offline'} /></dd>
              </div>
              <div>
                <dt className="text-sm text-gray-600 mb-1">Access Gateway</dt>
                <dd className="text-sm font-mono text-gray-900">
                  {device.access.connected ? device.access.gatewayName || device.access.gatewayId || '—' : 'Not connected'}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-600 mb-1">Connected Since</dt>
                <dd className="text-sm text-gray-900">
                  {device.access.connected && device.access.connectedAt ? formatDate(device.access.connectedAt, getDateFormatSettings()) : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-600 mb-1">Access Device Status</dt>
                <dd className="text-sm text-gray-900">{device.access.deviceStatus.replace(/_/g, ' ')}</dd>
              </div>
            </dl>
          </div>
        </div>
      )}

      {/* Connection Information */}
      {activePeer && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
              <Activity className="w-5 h-5 text-green-500" />
              <span>Active Connection</span>
            </h2>
          </div>
          <div className="p-6">
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <dt className="text-sm text-gray-600 mb-1">VPN IP Address</dt>
                <dd className="text-sm font-mono text-gray-900">{activePeer.assignedIp || 'Dynamic (IKEv2)'}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-600 mb-1">Connected Edge</dt>
                <dd className="text-sm font-mono text-gray-900">{activePeer.edgeName || activePeer.edgeId}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-600 mb-1">Allowed IPs</dt>
                <dd className="text-sm font-mono text-gray-900">{activePeer.allowedIps.join(', ')}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-600 mb-1">Connected Since</dt>
                <dd className="text-sm text-gray-900">
                  {activePeer.connectedAt ? formatDate(activePeer.connectedAt, getDateFormatSettings()) : 'Unknown'}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-600 mb-1 flex items-center space-x-1">
                  <Download className="w-3 h-3" />
                  <span>Downloaded</span>
                </dt>
                <dd className="text-sm text-gray-900">{formatBytes(activePeer.rxBytes)}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-600 mb-1 flex items-center space-x-1">
                  <Upload className="w-3 h-3" />
                  <span>Uploaded</span>
                </dt>
                <dd className="text-sm text-gray-900">{formatBytes(activePeer.txBytes)}</dd>
              </div>
              {activePeer.lastHandshakeAt && (
                <div>
                  <dt className="text-sm text-gray-600 mb-1">Last Handshake</dt>
                  <dd className="text-sm text-gray-900">
                    {formatDate(activePeer.lastHandshakeAt, getDateFormatSettings())}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      )}

      {/* VPN Peers */}
      {peers.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">VPN Peers</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Peer ID</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Edge</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">VPN IP</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Connected</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Disconnected</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Data Transfer</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {peers.map((peer) => (
                  <tr key={peer.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-mono text-gray-900">{peer.id.substring(0, 8)}...</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{peer.edgeName || peer.edgeId}</td>
                    <td className="px-6 py-4 text-sm font-mono text-gray-900">{peer.assignedIp || <span className="text-gray-400 italic">Dynamic</span>}</td>
                    <td className="px-6 py-4 text-sm">
                      <StatusBadge status={peer.enabled && peer.status === 'active' ? 'Online' : 'Offline'} />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {peer.connectedAt ? formatDate(peer.connectedAt, getDateFormatSettings()) : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {peer.disconnectedAt ? formatDate(peer.disconnectedAt, getDateFormatSettings()) : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      ↓{formatBytes(peer.rxBytes)} ↑{formatBytes(peer.txBytes)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Connection History Events */}
      {deviceDetail.connectionHistory && deviceDetail.connectionHistory.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Connection History</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Event</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Time</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">VPN IP</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Endpoint</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Data Transfer</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Reason</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {deviceDetail.connectionHistory.map((event) => (
                  <tr key={event.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${event.eventType === 'connected'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                          }`}
                      >
                        {event.eventType === 'connected' ? 'Connected' : 'Disconnected'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {formatDate(event.eventAt, getDateFormatSettings())}
                    </td>
                    <td className="px-6 py-4 text-sm font-mono text-gray-900">
                      {event.assignedIp || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm font-mono text-gray-500">
                      {event.endpoint || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      ↓{formatBytes(event.rxBytes)} ↑{formatBytes(event.txBytes)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {event.disconnectReason || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Device Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Device"
        size="md"
        footer={
          <div className="flex items-center justify-end space-x-3">
            <button onClick={() => setShowEditModal(false)} className="btn-secondary" disabled={savingEdit}>
              Cancel
            </button>
            <button
              onClick={handleSaveEdit}
              disabled={savingEdit || !editName.trim()}
              className="btn-primary inline-flex items-center space-x-2"
            >
              {savingEdit ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <span>Save Changes</span>
              )}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Device Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="My iPhone"
              className="input-base w-full"
              required
            />
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500">
              Other device properties (platform, OS version, fingerprint) are automatically detected and cannot be changed.
            </p>
          </div>
        </div>
      </Modal>

      {/* Group Assignment Modal */}
      <Modal
        isOpen={showGroupModal}
        onClose={() => setShowGroupModal(false)}
        title="Change Device Group"
        size="md"
        footer={
          <div className="flex items-center justify-end space-x-3">
            <button onClick={() => setShowGroupModal(false)} className="btn-secondary" disabled={assigningGroup}>
              Cancel
            </button>
            {canUpdate && (
              <button
                onClick={handleAssignGroup}
                disabled={assigningGroup}
                className="btn-primary inline-flex items-center space-x-2"
              >
                {assigningGroup ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <span>Update Group</span>
                )}
              </button>
            )}
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Assign this device to a group. The group determines which edge gateway the device connects to and what access policies apply.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Device Group</label>
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-64 overflow-y-auto">
              <label className={`flex items-center px-3 py-2 cursor-pointer hover:bg-gray-50 ${!selectedGroupId ? 'bg-primary-50' : ''}`}>
                <input
                  type="radio"
                  name="group"
                  checked={!selectedGroupId}
                  onChange={() => setSelectedGroupId('')}
                  className="mr-3 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-500">Unassigned</span>
              </label>
              {availableGroups.map((group) => {
                const capacityPct = group.max_devices ? ((group.device_count || 0) / group.max_devices) * 100 : 0;
                return (
                  <label
                    key={group.id}
                    className={`flex items-center px-3 py-2 cursor-pointer hover:bg-gray-50 ${selectedGroupId === group.id ? 'bg-primary-50' : ''}`}
                  >
                    <input
                      type="radio"
                      name="group"
                      checked={selectedGroupId === group.id}
                      onChange={() => setSelectedGroupId(group.id)}
                      className="mr-3 text-primary-600 focus:ring-primary-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-900">{group.name}</span>
                        {group.edge_id ? (
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                            group.edge_type === 'mtge' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {group.edge_type === 'mtge' ? 'MTGE' : 'Edge'}
                          </span>
                        ) : (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700">Unbound</span>
                        )}
                      </div>
                      <div className="flex items-center space-x-3">
                        {group.subnet_cidr && (
                          <span className="text-xs text-gray-500 font-mono">{group.subnet_cidr}</span>
                        )}
                        {group.max_devices && (
                          <span className={`text-xs ${capacityPct > 95 ? 'text-red-600' : capacityPct > 80 ? 'text-amber-600' : 'text-gray-500'}`}>
                            {group.device_count || 0}/{group.max_devices} devices
                          </span>
                        )}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Device"
        message={`Are you sure you want to delete "${device.name}"? This will remove the device and all its connection peers. This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleting}
      />

      {/* Revoke Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showRevokeConfirm}
        onClose={() => setShowRevokeConfirm(false)}
        onConfirm={handleRevoke}
        title="Revoke Device Access"
        message={`Revoke VPN access for "${device.name}"? Any active session will be dropped within seconds and the user cannot reconnect or re-register until you re-enable the device.`}
        confirmText="Revoke"
        variant="danger"
        isLoading={revoking}
      />

      {/* Re-enable Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showReenableConfirm}
        onClose={() => setShowReenableConfirm(false)}
        onConfirm={handleReenable}
        title="Re-enable Device"
        message={`Re-enable VPN access for "${device.name}"? The VPN app restores the session automatically within a minute — the user just needs to tap Connect.`}
        confirmText="Re-enable"
        variant="info"
        isLoading={reenabling}
      />
    </div>
  );
};

export default AppDeviceDetailPage;
